import React, { useState, useEffect } from 'react';
import Header from '../../layouts/Header';
import {
    Col,
    Row,
    Card,
    Table,
    Button,
    Form,
    Modal,
    Spinner,
    Badge,
    InputGroup,
    FormControl,
    Pagination,
    Dropdown
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import axios from '../../api/axios';
import DatePicker from "react-datepicker";
import { addDays, differenceInCalendarDays, startOfDay, endOfDay } from 'date-fns';

import { toast } from 'react-toastify';
import {
    Bell,
    Calendar,
    Clock,
    Store,
    Filter,
    Search,
    Plus,
    Edit,
    Trash2,
    RefreshCw,
    Tag,
    Users,
    Package,
    DollarSign,
    Image as ImageIcon,
    X,
    IndianRupee,
    ChevronFirst,
    ChevronLast,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';
import { format, isBefore, isAfter, isWithinInterval } from 'date-fns';
import { EyeFill } from 'react-bootstrap-icons';

const NotificationScheduler = () => {
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);

    const { access } = useSelector((state) => state.login);
    const [notificationAccess, setNotificationAccess] = useState([]);

    // Main states
    const [loading, setLoading] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [filteredNotifications, setFilteredNotifications] = useState([]);
    const [totalNotifications, setTotalNotifications] = useState(0);

    // Filter states
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('ALL');
    const [statusFilter, setStatusFilter] = useState('ALL');

    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [viewSingleData, setViewSingleData] = useState({});

    // Form states
    const [formData, setFormData] = useState({
        title: '',
        subTitle: '',
        startDate: '',
        endDate: '',
        time: '',
        outlet: null,
        fromDate: '',
        toDate: '',
        minPrice: '',
        maxPrice: '',
        foodType: '',
        userType: 'ALL', // Default to ALL users
        category: null,
        product: null,
        type: 'PUSH', // Default type set to PUSH 
        image: null
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Options states
    const [outlets, setOutlets] = useState([]);
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [totalPages, setTotalPages] = useState(1);
    const [paginationInfo, setPaginationInfo] = useState({
        page: 1,
        limit: 25,
        total: 0,
        totalPages: 1
    });

    // Items per page options
    const pageSizeOptions = [10, 25, 50, 100];

    const accessToken = localStorage.getItem('authToken');

    // Get permissions
    useEffect(() => {
        if (Array.isArray(access)) {
            const filteredAccess = access?.filter((perm) =>
                perm?.startsWith("NotificationScheduler:")
            );
            setNotificationAccess(filteredAccess);
        }
    }, [access]);

    // Custom styles for react-select
    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            border: '1px solid #dee2e6',
            borderRadius: '0.375rem',
            minHeight: '38px',
            backgroundColor: skin === 'dark' ? '#2a3042' : 'white',
            color: skin === 'dark' ? 'white' : 'black',
            '&:hover': {
                borderColor: '#86b7fe',
            },
        }),
        menu: (base) => ({
            ...base,
            backgroundColor: skin === 'dark' ? '#2a3042' : 'white',
            color: skin === 'dark' ? 'white' : 'black',
            zIndex: 9999,
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused
                ? (skin === 'dark' ? '#3a4155' : '#e9ecef')
                : (skin === 'dark' ? '#2a3042' : 'white'),
            color: skin === 'dark' ? 'white' : 'black',
            '&:hover': {
                backgroundColor: skin === 'dark' ? '#3a4155' : '#e9ecef',
            }
        }),
        singleValue: (base) => ({
            ...base,
            color: skin === 'dark' ? 'white' : 'black',
        }),
        placeholder: (base) => ({
            ...base,
            color: '#6c757d',
        }),
        input: (base) => ({
            ...base,
            color: skin === 'dark' ? 'white' : 'black',
        }),
    };

    // Fetch all data
    const fetchInitialData = async () => {
        await Promise.all([
            getNotifications(currentPage, itemsPerPage),
            getOutlets(),
            getCategories(),
            getProducts()
        ]);
    };

    // Fetch notifications with server-side pagination
    const getNotifications = async (page = currentPage, limit = itemsPerPage) => {
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };

            // Build query parameters - ONLY pagination parameters
            let queryParams = `?page=${page}&limit=${limit}`;

            // Remove all other filters from API call
            // All filtering will be done client-side

            const response = await axios.get(
                `notification-scheduler${queryParams}`,
                { headers }
            );

            console.log("Response of Notifications :", response);

            if (response?.data?.status === true) {
                const data = response?.data?.data || [];
                setNotifications(data || []);
                
                // Apply client-side filters to the new data
                applyAllFilters(data);
                
                // Set pagination info from response
                if (response?.data?.pagination) {
                    const { page, limit, total, totalPages } = response.data.pagination;
                    setPaginationInfo({
                        page: parseInt(page),
                        limit: parseInt(limit),
                        total: parseInt(total),
                        totalPages: parseInt(totalPages)
                    });
                    setCurrentPage(parseInt(page));
                    setItemsPerPage(parseInt(limit));
                    setTotalPages(parseInt(totalPages));
                    setTotalNotifications(parseInt(total));
                } else {
                    // Fallback if pagination data is not in response
                    setTotalNotifications(data.length || 0);
                }
            }
        } catch (error) {
            showError(error, 'Failed to fetch notifications');
        } finally {
            setLoading(false);
        }
    };

    // Apply ALL filters (search, type, status) on client-side
    const applyAllFilters = (data = notifications) => {
        let filtered = [...data];
        
        // Apply search filter
        if (searchQuery.trim() !== '') {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(notification =>
                (notification.title && notification.title.toLowerCase().includes(query)) ||
                (notification.subTitle && notification.subTitle.toLowerCase().includes(query)) ||
                (notification.outlet?.name && notification.outlet.name.toLowerCase().includes(query)) ||
                (notification.category?.name && notification.category.name.toLowerCase().includes(query)) ||
                (notification.product?.name && notification.product.name.toLowerCase().includes(query))
            );
        }

        // Apply type filter
        if (typeFilter !== 'ALL') {
            filtered = filtered.filter(notification =>
                notification.type === typeFilter
            );
        }

        // Apply status filter
        if (statusFilter !== 'ALL') {
            filtered = filtered.filter(notification => {
                const status = getNotificationStatus(notification.startDate, notification.endDate);
                return status === statusFilter;
            });
        }

        setFilteredNotifications(filtered);
    };

    // Apply filters when any filter changes
    useEffect(() => {
        if (notifications.length > 0) {
            applyAllFilters();
        }
    }, [searchQuery, typeFilter, statusFilter]);

    // Fetch outlets
    const getOutlets = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("outlet", { headers });

            if (response?.data?.status === true) {
                const outletsData = response?.data?.data?.outlets || [];
                const options = outletsData?.map(outlet => ({
                    value: outlet._id,
                    label: outlet.name || outlet.outletName
                }));
                setOutlets(options);
            }
        } catch (error) {
            console.error('Error fetching outlets:', error);
        }
    };

    // Fetch categories
    const getCategories = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("masters/category/get", { headers });

            if (response?.data?.status === true) {
                const categoriesData = response?.data?.data?.categories || [];
                const options = categoriesData.map(category => ({
                    value: category._id,
                    label: category.name || category.categoryName
                }));
                setCategories(options);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    // Fetch products with search
    const getProducts = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`
            };
            const response = await axios.get(`product`, { headers });

            if (response?.data?.status === true) {
                console.log("Products Response :", response);
                const productsData = response?.data?.data?.products || [];
                const options = productsData?.map(product => ({
                    value: product._id,
                    label: product.name,
                    // foodType: product.foodType
                }));
                setProducts(productsData);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
        }
        return [];
    };

    // Load products for async select
    const loadProducts = async (inputValue) => {
        return await getProducts(inputValue);
    };

    // Handle form input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Handle User Type change
        if (name === 'userType') {
            const restricted = value !== 'ALL' && value !== 'REPEATED_USERS_DAYS';

            setFormData(prev => ({
                ...prev,
                userType: value,
                ...(restricted && {
                    foodType: '',
                    category: null,
                    product: null,
                    minPrice: '',
                    maxPrice: ''
                })
            }));
            return;
        }

        // Default handler
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle select change
    const handleSelectChange = (name, selectedOption) => {
        setFormData(prev => ({
            ...prev,
            [name]: selectedOption
        }));
    };

    // Handle image upload
    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Check file type
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
        if (!validTypes.includes(file.type)) {
            toast.error('Please upload a valid image file (JPEG, PNG, JPG, GIF)', {
                position: "top-right",
                autoClose: 3000,
            });
            return;
        }

        // Check file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image size should be less than 5MB', {
                position: "top-right",
                autoClose: 3000,
            });
            return;
        }

        setFormData(prev => ({
            ...prev,
            image: file
        }));

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    // Remove image
    const handleRemoveImage = () => {
        setFormData(prev => ({
            ...prev,
            image: null
        }));
        setImagePreview(null);
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            title: '',
            subTitle: '',
            startDate: '',
            endDate: '',
            time: '',
            outlet: null,
            fromDate: '',
            toDate: '',
            minPrice: '',
            maxPrice: '',
            type: 'PUSH',
            foodType: '',
            userType: 'ALL',
            category: null,
            product: null,
            image: null
        });
        setImagePreview(null);
    };

    // Open add modal
    const handleAddNotification = () => {
        resetForm();
        setShowAddModal(true);
    };

    // Open View Modal
    const handleViewNotification = (notification) => {
        console.log("View Modal Data :", notification);
        setViewSingleData(notification);
        setShowViewModal(true);
    }

    // Open delete modal
    const handleDeleteNotification = (notification) => {
        setSelectedNotification(notification);
        setShowDeleteModal(true);
    };

    // Get notification status
    const getNotificationStatus = (startDate, endDate) => {
    try {
        const now = new Date();

        const start = startOfDay(new Date(startDate));
        const end = endOfDay(new Date(endDate)); // KEY FIX

        if (isBefore(now, start)) {
            return 'UPCOMING';
        } else if (isWithinInterval(now, { start, end })) {
            return 'ACTIVE';
        } else {
            return 'EXPIRED';
        }
    } catch (error) {
        return 'EXPIRED';
    }
};

    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    // Handle search submit - now only triggers client-side filtering
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        // No need to call API, filters are applied client-side
    };

    // Handle filter changes - now only triggers client-side filtering
    const handleTypeFilterChange = (e) => {
        const value = e.target.value;
        setTypeFilter(value);
        // No need to call API, filters are applied client-side
    };

    const handleStatusFilterChange = (e) => {
        const value = e.target.value;
        setStatusFilter(value);
        // No need to call API, filters are applied client-side
    };

    // Clear all filters
    const handleClearFilters = () => {
        setSearchQuery('');
        setTypeFilter('ALL');
        setStatusFilter('ALL');
        // No need to call API, filters are applied client-side
    };

    // Handle page change
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        getNotifications(pageNumber, itemsPerPage);
    };

    // Handle items per page change
    const handleItemsPerPageChange = (size) => {
        setItemsPerPage(size);
        setCurrentPage(1); // Reset to first page when changing page size
        getNotifications(1, size);
    };

    // Go to first page
    const goToFirstPage = () => {
        if (currentPage > 1) {
            handlePageChange(1);
        }
    };

    // Go to last page
    const goToLastPage = () => {
        if (currentPage < totalPages) {
            handlePageChange(totalPages);
        }
    };

    // Go to previous page
    const goToPreviousPage = () => {
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    };

    // Go to next page
    const goToNextPage = () => {
        if (currentPage < totalPages) {
            handlePageChange(currentPage + 1);
        }
    };

    // Calculate page range for pagination display
    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            // Show all pages
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            // Show limited pages with ellipsis
            let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
            let endPage = startPage + maxPagesToShow - 1;

            if (endPage > totalPages) {
                endPage = totalPages;
                startPage = Math.max(1, endPage - maxPagesToShow + 1);
            }

            for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(i);
            }
        }

        return pageNumbers;
    };

    // Helper function to format date as YYYY-MM-DD
    const formatDateForAPI = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toISOString().split('T')[0];
    };

    // Helper to build FormData cleanly
    const buildFormData = (formData, selectedNotification, showEditModal) => {
        const formDataToSend = new FormData();

        Object.keys(formData).forEach((key) => {
            const value = formData[key];

            // outlet
            if (key === 'outlet' && value) {
                formDataToSend.append('outlet', value.value);
            }
            // category
            else if (key === 'category' && value) {
                formDataToSend.append('category', value.value);
            }
            // product
            else if (key === 'product' && value) {
                formDataToSend.append('product', value.value);
            }
            // image
            else if (key === 'image' && value) {
                formDataToSend.append('image', value);
            }
            // format date fields
            else if (
                ['startDate', 'endDate', 'fromDate', 'toDate'].includes(key)
            ) {
                if (value) {
                    formDataToSend.append(key, formatDateForAPI(value));
                }
            }
            // append other fields
            else if (
                value !== null &&
                value !== undefined &&
                value !== ''
            ) {
                formDataToSend.append(key, value);
            }
        });

        // append id if edit mode
        if (showEditModal && selectedNotification) {
            formDataToSend.append('id', selectedNotification._id);
        }

        return formDataToSend;
    };

    // Main Submit Handler
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        const restrictedUserType =
            formData.userType !== 'ALL' &&
            formData.userType !== 'REPEATED_USERS_DAYS';

        if (restrictedUserType) {
            if (formData.foodType && formData.foodType !== 'BOTH') {
                toast.error('foodType is not allowed for selected user type');
                return;
            }

            if (formData.category) {
                toast.error('category is not allowed for selected user type');
                return;
            }

            if (formData.product) {
                toast.error('product is not allowed for selected user type');
                return;
            }

            if (formData.minPrice || formData.maxPrice) {
                toast.error('price range is not allowed for selected user type');
                return;
            }
        }

        setSubmitting(true);

        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'multipart/form-data'
            };

            // Build FormData properly
            const formDataToSend = buildFormData(
                formData,
                selectedNotification,
                showEditModal
            );

            let response;

            if (showEditModal && selectedNotification) {
                response = await axios.put(
                    'notification-scheduler',
                    formDataToSend,
                    { headers }
                );
            } else {
                response = await axios.post(
                    'notification-scheduler',
                    formDataToSend,
                    { headers }
                );
            }

            if (response?.data?.status === true) {
                toast.success(
                    response?.data?.message ||
                    'Notification saved successfully!'
                );

                setShowAddModal(false);
                setShowEditModal(false);
                resetForm();
                getNotifications(currentPage, itemsPerPage);
            }

        } catch (error) {
            handleApiError(error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleApiError = (error) => {
        let errorMessage = 'An error occurred';
        if (error.response?.status === 406) {
            errorMessage = error.response?.data?.error?.message || error.response?.data?.error;
        } else if (error.response?.status === 412) {
            errorMessage = error.response?.data?.data?.message;
        } else if (error.response?.status === 500) {
            errorMessage = error.response?.data?.error;
        }

        toast.error(errorMessage, {
            position: "top-right",
            autoClose: 3000,
            theme: "light",
        });
    };

    // Delete notification
    const handleDelete = async () => {
        setDeleteLoading(true);

        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            };

            const data = {
                id: selectedNotification._id
            };

            const response = await axios.delete('notification-scheduler', { data, headers });

            if (response?.data?.status === true) {
                toast.success('Notification deleted successfully!', {
                    position: "top-right",
                    autoClose: 3000,
                });
                setShowDeleteModal(false);
                getNotifications(currentPage, itemsPerPage);
            }
        } catch (error) {
            if (error.response.status === 403) {
                toast.error(error?.response?.data?.message, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }

            if (error.response.status === 404) {
                toast.error(error?.response?.data?.message, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }

            if (error.response.status === 406) {
                toast.error(error?.response?.data?.message, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }

            if (error.response.status === 412) {
                toast.error((error?.response?.data?.message || error?.response?.data?.data?.message), {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }

            if (error.response && error.response?.data?.status === false) {
                let errorMessage = error.response.data.error;
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        } finally {
            setDeleteLoading(false);
        }
    };

    // Error handler
    const showError = (error, defaultMessage) => {
        const errorMessage = error.response?.data?.error || error.response?.data?.message || defaultMessage;
        toast.error(errorMessage, {
            position: "top-right",
            autoClose: 3000,
        });
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';

        const date = new Date(dateString);

        return date.toLocaleDateString('en-GB', {
            timeZone: 'UTC',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    // Format datetime
    const formatDateTime = (dateString) => {
        try {
            return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
        } catch {
            return dateString;
        }
    };

    // Get status badge
    const getStatusBadge = (startDate, endDate) => {
        const status = getNotificationStatus(startDate, endDate);
        const variants = {
            UPCOMING: { bg: 'warning', text: 'Upcoming' },
            ACTIVE: { bg: 'success', text: 'Active' },
            EXPIRED: { bg: 'secondary', text: 'Expired' }
        };
        const variant = variants[status] || { bg: 'secondary', text: 'Unknown' };
        return <Badge bg={variant.bg}>{variant.text}</Badge>;
    };

    // Get type badge
    const getTypeBadge = (type) => {
        const variants = {
            PUSH: 'primary',
            EMAIL: 'info'
        };
        return <Badge bg={variants[type] || 'secondary'}>{type}</Badge>;
    };

    // Initialize
    useEffect(() => {
        fetchInitialData();
    }, []);

    return (
        <>
            <Header onSkin={setSkin} />

            <div className="main main-app p-3 p-lg-4">
                <div className="container shadow-sm" style={{ backgroundColor: "white" }}>
                    {/* Header */}
                    <Row className="pt-3">
                        <Col>
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <div className="d-flex align-items-center">
                                    <Bell className="me-3" size={26} color="#0d6efd" />
                                    <h4 className="fw-semibold mb-0">NOTIFICATION SCHEDULER</h4>
                                </div>
                                <Button
                                    variant="primary"
                                    onClick={handleAddNotification}
                                >
                                    <Plus size={16} className="me-2" />
                                    Schedule Notification
                                </Button>
                            </div>
                        </Col>
                        <hr />
                    </Row>

                    {/* Filters Section */}
                    <Row className="mb-4">
                        <Col md={4}>
                            <Form onSubmit={handleSearchSubmit}>
                                <Form.Label className="fw-semibold">Search</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <Search size={18} />
                                    </InputGroup.Text>
                                    <FormControl
                                        placeholder="Search notifications..."
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit(e)}
                                    />
                                </InputGroup>
                            </Form>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="fw-semibold">Type Filter</Form.Label>
                                <Form.Select
                                    value={typeFilter}
                                    onChange={handleTypeFilterChange}
                                >
                                    <option value="ALL">All Types</option>
                                    <option value="PUSH">Push Notifications</option>
                                    <option value="EMAIL">Email Notifications</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="fw-semibold">Status Filter</Form.Label>
                                <Form.Select
                                    value={statusFilter}
                                    onChange={handleStatusFilterChange}
                                >
                                    <option value="ALL">All Status</option>
                                    <option value="ACTIVE">Active</option>
                                    <option value="UPCOMING">Upcoming</option>
                                    <option value="EXPIRED">Expired</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={2} className="d-flex align-items-end">
                            <Button
                                variant="outline-danger"
                                className="w-100"
                                onClick={handleClearFilters}
                                disabled={searchQuery === '' && typeFilter === 'ALL' && statusFilter === 'ALL'}
                            >
                                <Filter size={16} className="me-2" />
                                Clear Filters
                            </Button>
                        </Col>
                    </Row>

                    {/* Statistics Info */}
                    <Row className="mb-3">
                        <Col>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="mb-0">
                                        Showing <strong>
                                            {Math.min((currentPage - 1) * itemsPerPage + 1, filteredNotifications.length)} -{' '}
                                            {Math.min(currentPage * itemsPerPage, filteredNotifications.length)}
                                        </strong> of <strong>{filteredNotifications.length}</strong> filtered notifications
                                        {filteredNotifications.length !== notifications.length && (
                                            <span className="text-muted ms-2">
                                                (from {notifications.length} total)
                                            </span>
                                        )}
                                    </h6>
                                </div>
                                <div className="d-flex gap-2 align-items-center">
                                    {/* Records per page dropdown */}
                                    <div className="d-flex align-items-center">
                                        <span className="me-2">Show:</span>
                                        <Dropdown>
                                            <Dropdown.Toggle variant="outline-secondary" size="sm" id="dropdown-page-size">
                                                {itemsPerPage}
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                {pageSizeOptions.map(size => (
                                                    <Dropdown.Item
                                                        key={size}
                                                        active={itemsPerPage === size}
                                                        onClick={() => handleItemsPerPageChange(size)}
                                                    >
                                                        {size}
                                                    </Dropdown.Item>
                                                ))}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                        <span className="ms-2">records per page</span>
                                    </div>

                                    <div className="d-flex gap-2">
                                        {(searchQuery !== '' || typeFilter !== 'ALL' || statusFilter !== 'ALL') && (
                                            <Button
                                                variant="outline-info"
                                                size="sm"
                                                onClick={handleClearFilters}
                                            >
                                                Show All
                                            </Button>
                                        )}
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => getNotifications(currentPage, itemsPerPage)}
                                            disabled={loading}
                                        >
                                            <RefreshCw size={16} className="me-2" />
                                            {loading ? 'Refreshing...' : 'Refresh'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Notifications Table */}
                    <Row>
                        <Col>
                            <Card className='mb-3'>
                                <Card.Header>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h6 className="mb-0">Scheduled Notifications</h6>
                                        <div className="d-flex gap-2">
                                            <Badge bg="success" className="px-2 py-1">
                                                Active: {notifications.filter(n => getNotificationStatus(n.startDate, n.endDate) === 'ACTIVE').length}
                                            </Badge>
                                            <Badge bg="warning" className="px-2 py-1">
                                                Upcoming: {notifications.filter(n => getNotificationStatus(n.startDate, n.endDate) === 'UPCOMING').length}
                                            </Badge>
                                            <Badge bg="secondary" className="px-2 py-1">
                                                Expired: {notifications.filter(n => getNotificationStatus(n.startDate, n.endDate) === 'EXPIRED').length}
                                            </Badge>
                                        </div>
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    {loading ? (
                                        <div className="text-center py-4">
                                            <Spinner animation="border" variant="primary" />
                                            <p className="mt-2 mb-0">Loading notifications...</p>
                                        </div>
                                    ) : filteredNotifications?.length > 0 ? (
                                        <>
                                            <div className="table-responsive">
                                                <Table striped bordered hover>
                                                    <thead>
                                                        <tr>
                                                            <th>Sr No.</th>
                                                            <th>Image</th>
                                                            <th>Title</th>
                                                            <th>Type</th>
                                                            <th>Schedule</th>
                                                            <th>Status</th>
                                                            <th>Created</th>
                                                            <th className="text-center">Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {filteredNotifications?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((notification, index) => (
                                                            <tr key={notification._id}>
                                                                <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                                                <td>
                                                                    {notification.image ? (
                                                                        <div className="text-center">
                                                                            <img
                                                                                src={notification.image}
                                                                                alt={notification.title || "Notification"}
                                                                                className="img-thumbnail"
                                                                                style={{
                                                                                    width: '60px',
                                                                                    height: '60px',
                                                                                    objectFit: 'cover',
                                                                                    cursor: 'pointer'
                                                                                }}
                                                                                title="Click to view larger image"
                                                                            />
                                                                        </div>
                                                                    ) : (
                                                                        <div className="text-center">
                                                                            <div
                                                                                className="bg-light d-flex align-items-center justify-content-center"
                                                                                style={{
                                                                                    width: '60px',
                                                                                    height: '60px',
                                                                                    borderRadius: '0.25rem'
                                                                                }}
                                                                            >
                                                                                <ImageIcon size={24} className="text-muted" />
                                                                            </div>
                                                                            <small className="d-block text-muted mt-1">No Image</small>
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <div className="fw-semibold">{notification.title}</div>
                                                                    <small className="text-muted">{notification.subTitle}</small>
                                                                </td>
                                                                <td>
                                                                    {getTypeBadge(notification.type)}
                                                                </td>
                                                                <td>
                                                                    <div>
                                                                        <small className="text-muted d-block">
                                                                            <Calendar size={12} className="me-1" />
                                                                            {formatDate(notification.startDate)} - {formatDate(notification.endDate)}
                                                                        </small>
                                                                        <small className="text-muted">
                                                                            <Clock size={12} className="me-1" />
                                                                            {notification.time}
                                                                        </small>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    {getStatusBadge(notification.startDate, notification.endDate)}
                                                                </td>
                                                                <td>
                                                                    <small className="text-muted">
                                                                        {formatDateTime(notification.createdAt)}
                                                                    </small>
                                                                </td>
                                                                <td className="text-center">
                                                                    <div className="d-flex gap-2 justify-content-center">
                                                                        <Button
                                                                            variant="outline-primary"
                                                                            size="sm"
                                                                            onClick={() => handleViewNotification(notification)}
                                                                            title="View Details"
                                                                        >
                                                                            <EyeFill size={14} />
                                                                        </Button>
                                                                        <Button
                                                                            variant="outline-danger"
                                                                            size="sm"
                                                                            onClick={() => handleDeleteNotification(notification)}
                                                                            title="Delete"
                                                                        >
                                                                            <Trash2 size={14} />
                                                                        </Button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>

                                            {/* Pagination - Bottom */}
                                            {Math.ceil(filteredNotifications.length / itemsPerPage) > 1 && (
                                                <div className="d-flex justify-content-between align-items-center mt-4">
                                                    <div>
                                                        <span className="text-muted">
                                                            Page {currentPage} of {Math.ceil(filteredNotifications.length / itemsPerPage)} |{' '}
                                                            {filteredNotifications.length} filtered records
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <Pagination className="mb-0">
                                                            {/* First Page Button */}
                                                            <Pagination.First
                                                                onClick={() => setCurrentPage(1)}
                                                                disabled={currentPage === 1}
                                                                title="First Page"
                                                            >
                                                                <ChevronFirst size={14} />
                                                            </Pagination.First>

                                                            {/* Previous Page Button */}
                                                            <Pagination.Prev
                                                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                                disabled={currentPage === 1}
                                                                title="Previous Page"
                                                            >
                                                                <ChevronLeft size={14} />
                                                            </Pagination.Prev>

                                                            {/* Page Numbers */}
                                                            {(() => {
                                                                const totalFilteredPages = Math.ceil(filteredNotifications.length / itemsPerPage);
                                                                const pageNumbers = [];
                                                                const maxPagesToShow = 5;
                                                                
                                                                if (totalFilteredPages <= maxPagesToShow) {
                                                                    for (let i = 1; i <= totalFilteredPages; i++) {
                                                                        pageNumbers.push(i);
                                                                    }
                                                                } else {
                                                                    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
                                                                    let endPage = startPage + maxPagesToShow - 1;

                                                                    if (endPage > totalFilteredPages) {
                                                                        endPage = totalFilteredPages;
                                                                        startPage = Math.max(1, endPage - maxPagesToShow + 1);
                                                                    }

                                                                    for (let i = startPage; i <= endPage; i++) {
                                                                        pageNumbers.push(i);
                                                                    }
                                                                }

                                                                return pageNumbers.map(number => (
                                                                    <Pagination.Item
                                                                        key={number}
                                                                        active={number === currentPage}
                                                                        onClick={() => setCurrentPage(number)}
                                                                    >
                                                                        {number}
                                                                    </Pagination.Item>
                                                                ));
                                                            })()}

                                                            {/* Next Page Button */}
                                                            <Pagination.Next
                                                                onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredNotifications.length / itemsPerPage), prev + 1))}
                                                                disabled={currentPage >= Math.ceil(filteredNotifications.length / itemsPerPage)}
                                                                title="Next Page"
                                                            >
                                                                <ChevronRight size={14} />
                                                            </Pagination.Next>

                                                            {/* Last Page Button */}
                                                            <Pagination.Last
                                                                onClick={() => setCurrentPage(Math.ceil(filteredNotifications.length / itemsPerPage))}
                                                                disabled={currentPage >= Math.ceil(filteredNotifications.length / itemsPerPage)}
                                                                title="Last Page"
                                                            >
                                                                <ChevronLast size={14} />
                                                            </Pagination.Last>
                                                        </Pagination>
                                                    </div>

                                                    <div className="d-flex align-items-center">
                                                        <span className="me-2">Go to page:</span>
                                                        <Form.Control
                                                            type="number"
                                                            min="1"
                                                            max={Math.ceil(filteredNotifications.length / itemsPerPage)}
                                                            value={currentPage}
                                                            onChange={(e) => {
                                                                const page = parseInt(e.target.value);
                                                                if (page >= 1 && page <= Math.ceil(filteredNotifications.length / itemsPerPage)) {
                                                                    setCurrentPage(page);
                                                                }
                                                            }}
                                                            style={{ width: '80px' }}
                                                            size="sm"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="text-center py-4">
                                            <Bell size={48} className="text-muted mb-3" />
                                            {searchQuery !== '' || typeFilter !== 'ALL' || statusFilter !== 'ALL' ? (
                                                <>
                                                    <h5>No Matching Notifications Found</h5>
                                                    <p className="text-muted mb-4">
                                                        No notifications match your current filters. Try adjusting your search criteria.
                                                    </p>
                                                    <Button
                                                        variant="outline-primary"
                                                        onClick={handleClearFilters}
                                                    >
                                                        Clear All Filters
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <h5>No Notifications Found</h5>
                                                    <p className="text-muted mb-4">
                                                        No scheduled notifications found. Schedule your first notification!
                                                    </p>
                                                    <Button
                                                        variant="primary"
                                                        onClick={handleAddNotification}
                                                    >
                                                        <Plus size={16} className="me-2" />
                                                        Schedule Notification
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>


                {/* Add/Edit Modal */}
                <Modal show={showAddModal || showEditModal} onHide={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    resetForm();
                }} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <Bell size={20} className="me-2" />
                            {showEditModal ? 'Edit Notification' : 'Schedule New Notification'}
                        </Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleSubmit}>
                        <Modal.Body>
                            <Row>
                                {/* Basic Information */}
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">Title <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleInputChange}
                                            placeholder="Enter notification title"
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">Subtitle</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="subTitle"
                                            value={formData.subTitle}
                                            onChange={handleInputChange}
                                            placeholder="Enter notification subtitle"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Schedule Information */}
                            <Row>
                                {/* <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">Start Date <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="startDate"
                                            value={formData.startDate}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">End Date <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="endDate"
                                            value={formData.endDate}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </Form.Group>
                                </Col> */}
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">
                                            Start Date <span className="text-danger">*</span>
                                        </Form.Label>
                                        <DatePicker
                                            selected={formData.startDate}
                                            onChange={(date) =>
                                                setFormData(prev => ({
                                                    ...prev,
                                                    startDate: date,
                                                    endDate:
                                                        prev.endDate && prev.endDate < date
                                                            ? null
                                                            : prev.endDate
                                                }))
                                            }
                                            dateFormat="yyyy-MM-dd"
                                            placeholderText="Select start date"
                                            className="form-control"
                                            required
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">
                                            End Date <span className="text-danger">*</span>
                                        </Form.Label>
                                        <DatePicker
                                            selected={formData.endDate}
                                            onChange={(date) =>
                                                setFormData(prev => ({
                                                    ...prev,
                                                    endDate: date
                                                }))
                                            }
                                            dateFormat="yyyy-MM-dd"
                                            placeholderText="Select end date"
                                            className="form-control"
                                            minDate={formData.startDate}
                                            disabled={!formData.startDate}
                                            required
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">Time <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="time"
                                            name="time"
                                            value={formData.time}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* User Filters Section */}
                            <Row className="mb-3">
                                <Col>
                                    <div className="border rounded p-3 mb-3">
                                        <h6 className="fw-semibold mb-3">User Filters</h6>

                                        {/* User Type */}
                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">User Type <span className="text-danger">*</span></Form.Label>
                                                    <Form.Select
                                                        name="userType"
                                                        value={formData.userType}
                                                        onChange={handleInputChange}
                                                        required
                                                    >
                                                        <option value="ALL">All Users</option>
                                                        <option value="JOINING_COUPON_ONE_ORDER">Joining Coupon (One Order)</option>
                                                        <option value="COUPON_ORDERS">Coupon Orders</option>
                                                        <option value="NO_COUPON_ORDERS">No Coupon Orders</option>
                                                        <option value="REFERRING_USERS">Referring Users</option>
                                                        <option value="REFERRED_USERS">Referred Users</option>
                                                        <option value="REPEATED_USERS_DAYS">Repeated Users (Days)</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Outlet</Form.Label>
                                                    <Select
                                                        options={outlets}
                                                        value={formData.outlet}
                                                        onChange={(selected) => handleSelectChange('outlet', selected)}
                                                        placeholder="Select outlet..."
                                                        styles={customSelectStyles}
                                                        isClearable
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        {/* Date Range Filter */}
                                        <Row className="mb-3">
                                            {/* <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">From Date <span className='text-danger'>*</span></Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="fromDate"
                                                        value={formData.fromDate}
                                                        onChange={handleInputChange}
                                                        placeholder="Filter users from date"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">To Date <span className='text-danger'>*</span></Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="toDate"
                                                        value={formData.toDate}
                                                        onChange={handleInputChange}
                                                        placeholder="Filter users to date"
                                                    />
                                                </Form.Group>
                                            </Col> */}
                                            <Col md={6}>
                                                <Form.Group>
                                                    <div className='d-flex flex-column'>
                                                        <Form.Label className="fw-semibold">
                                                            From Date <span className="text-danger">*</span>
                                                        </Form.Label>
                                                        <DatePicker
                                                            selected={formData.fromDate}
                                                            onChange={(date) =>
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    fromDate: date,
                                                                    toDate:
                                                                        prev.toDate &&
                                                                            differenceInCalendarDays(prev.toDate, date) > 60
                                                                            ? null
                                                                            : prev.toDate
                                                                }))
                                                            }
                                                            dateFormat="yyyy-MM-dd"
                                                            placeholderText="Select start date"
                                                            className="form-control"
                                                            maxDate={new Date()}
                                                        />
                                                    </div>
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <div className='d-flex flex-column'>
                                                        <Form.Label className="fw-semibold">
                                                            To Date <span className="text-danger">*</span>
                                                        </Form.Label>
                                                        <DatePicker
                                                            selected={formData.toDate}
                                                            onChange={(date) =>
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    toDate: date
                                                                }))
                                                            }
                                                            dateFormat="yyyy-MM-dd"
                                                            placeholderText="Select end date"
                                                            className="form-control"
                                                            minDate={formData.fromDate}
                                                            maxDate={
                                                                formData.fromDate
                                                                    ? addDays(formData.fromDate, 60)
                                                                    : null
                                                            }
                                                            disabled={!formData.fromDate}
                                                        />
                                                    </div>
                                                </Form.Group>
                                            </Col>

                                        </Row>

                                        {/* Additional Filters */}
                                        <Row className="mb-3">
                                            {!(['JOINING_COUPON_ONE_ORDER', 'COUPON_ORDERS', 'NO_COUPON_ORDERS', 'REFERRING_USERS', 'REFERRED_USERS']).includes(formData?.userType) && (
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Preferred Taste</Form.Label>
                                                        <Form.Select
                                                            name="foodType"
                                                            value={formData.foodType}
                                                            onChange={handleInputChange}
                                                        >
                                                            <option value="">Select</option>
                                                            <option value="BOTH">BOTH</option>
                                                            <option value="FOOD">FOOD</option>
                                                            <option value="BEVERAGE">BEVERAGE</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                            )}
                                            {!(['JOINING_COUPON_ONE_ORDER', 'COUPON_ORDERS', 'NO_COUPON_ORDERS', 'REFERRING_USERS', 'REFERRED_USERS']).includes(formData?.userType) && (
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Price Range</Form.Label>
                                                        <div className="d-flex gap-2">
                                                            <Form.Control
                                                                type="number"
                                                                name="minPrice"
                                                                value={formData.minPrice}
                                                                onChange={handleInputChange}
                                                                placeholder="Min Price (₹)"
                                                                min="1"
                                                                step="any"
                                                            />
                                                            <Form.Control
                                                                type="number"
                                                                name="maxPrice"
                                                                value={formData.maxPrice}
                                                                onChange={handleInputChange}
                                                                placeholder="Max Price (₹)"
                                                                min="1"
                                                                step="any"
                                                            />
                                                        </div>
                                                    </Form.Group>
                                                </Col>
                                            )}
                                        </Row>

                                        {/* Category and Product Filters */}
                                        {!(['JOINING_COUPON_ONE_ORDER', 'COUPON_ORDERS', 'NO_COUPON_ORDERS', 'REFERRING_USERS', 'REFERRED_USERS']).includes(formData?.userType) && (
                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Category</Form.Label>
                                                        <Select
                                                            options={categories}
                                                            value={formData.category}
                                                            onChange={(selected) => handleSelectChange('category', selected)}
                                                            placeholder="Select category..."
                                                            styles={customSelectStyles}
                                                            isClearable
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Product</Form.Label>
                                                        <Select
                                                            // cacheOptions
                                                            // defaultOptions

                                                            options={products?.map(product => ({
                                                                value: product._id,
                                                                label: product.name
                                                            }))}
                                                            value={formData.product}
                                                            onChange={(selected) => handleSelectChange('product', selected)}
                                                            placeholder="Search and select product..."
                                                            styles={customSelectStyles}
                                                            isClearable
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        )}

                                        {/* Outlet Filter */}
                                        {/* <Row>
                                            <Col>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Outlet</Form.Label>
                                                    <Select
                                                        options={outlets}
                                                        value={formData.outlet}
                                                        onChange={(selected) => handleSelectChange('outlet', selected)}
                                                        placeholder="Select outlet..."
                                                        styles={customSelectStyles}
                                                        isClearable
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row> */}
                                    </div>
                                </Col>
                            </Row>

                            {/* Image Upload */}
                            <Row className="mb-3">
                                <Col>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold">Notification Image</Form.Label>
                                        {imagePreview ? (
                                            <div className="position-relative">
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    className="img-fluid rounded mb-2"
                                                    style={{ maxHeight: '200px' }}
                                                />
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    className="position-absolute top-0 end-0 m-2"
                                                    onClick={handleRemoveImage}
                                                >
                                                    <X size={14} />
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="border rounded p-4 text-center">
                                                <ImageIcon size={48} className="text-muted mb-2" />
                                                <p className="text-muted mb-2">Upload notification image (Max 5MB)</p>
                                                <Form.Control
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleImageUpload}
                                                />
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowAddModal(false);
                                    setShowEditModal(false);
                                    resetForm();
                                }}
                                disabled={submitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        {showEditModal ? 'Updating...' : 'Scheduling...'}
                                    </>
                                ) : (
                                    showEditModal ? 'Update Notification' : 'Schedule Notification'
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Delete</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Are you sure you want to delete this notification?</p>
                        <div className="alert alert-warning">
                            <strong>Title:</strong> {selectedNotification?.title}<br />
                            <strong>Type:</strong> {selectedNotification?.type}<br />
                            <strong>Schedule:</strong> {selectedNotification?.startDate ? formatDate(selectedNotification.startDate) : 'N/A'} - {selectedNotification?.endDate ? formatDate(selectedNotification.endDate) : 'N/A'}
                        </div>
                        <p className="text-muted small">
                            This action cannot be undone. The notification will be permanently deleted.
                        </p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={() => setShowDeleteModal(false)}
                            disabled={deleteLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleDelete}
                            disabled={deleteLoading}
                        >
                            {deleteLoading ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Deleting...
                                </>
                            ) : (
                                "Delete Notification"
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* View Details Modal */}
                {showViewModal && (
                    <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <Bell size={20} className="me-2" />
                                Notification Details
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {viewSingleData ? (
                                <div className="notification-details">
                                    {/* Header with Title and Status */}
                                    <div className="d-flex align-items-center justify-content-between mb-4">
                                        <div>
                                            <h4 className="fw-bold mb-1">{viewSingleData.title}</h4>
                                            <p className="text-muted mb-0">{viewSingleData.subTitle}</p>
                                        </div>
                                        <div>
                                            {getTypeBadge(viewSingleData.type)} <div></div>
                                            {getStatusBadge(viewSingleData.startDate, viewSingleData.endDate)}
                                        </div>
                                    </div>

                                    {/* Image Preview */}
                                    {viewSingleData.image && (
                                        <div className="text-center mb-4">
                                            <img
                                                src={viewSingleData.image}
                                                alt="Notification"
                                                className="img-fluid rounded"
                                                style={{ maxHeight: '250px', objectFit: 'contain' }}
                                            />
                                        </div>
                                    )}

                                    {/* Details in Cards */}
                                    <Row>
                                        {/* Schedule Information */}
                                        <Col md={6}>
                                            <Card className="mb-3">
                                                <Card.Header className="bg-light">
                                                    <h6 className="mb-0">
                                                        <Calendar size={16} className="me-2" />
                                                        Schedule Information
                                                    </h6>
                                                </Card.Header>
                                                <Card.Body>
                                                    <div className="mb-2">
                                                        <small className="text-muted d-block">Start Date:</small>
                                                        <strong>{formatDate(viewSingleData.startDate)}</strong>
                                                    </div>
                                                    <div className="mb-2">
                                                        <small className="text-muted d-block">End Date:</small>
                                                        <strong>{formatDate(viewSingleData.endDate)}</strong>
                                                    </div>
                                                    <div className="mb-2">
                                                        <small className="text-muted d-block">Time:</small>
                                                        <strong>{viewSingleData.time}</strong>
                                                    </div>
                                                    <div className="mb-2">
                                                        <small className="text-muted d-block">Type:</small>
                                                        <strong>{viewSingleData.type}</strong>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>

                                        {/* User Filters */}
                                        <Col md={6}>
                                            <Card className="mb-3">
                                                <Card.Header className="bg-light">
                                                    <h6 className="mb-0">
                                                        <Users size={16} className="me-2" />
                                                        User Filters
                                                    </h6>
                                                </Card.Header>
                                                <Card.Body>
                                                    {viewSingleData.userType ? (
                                                        <div className="mb-2">
                                                            <small className="text-muted d-block">User Type:</small>
                                                            <strong>
                                                                {viewSingleData.userType === 'JOINING_COUPON_ONE_ORDER' ? 'Joining Coupon (One Order)' :
                                                                    viewSingleData.userType === 'COUPON_ORDERS' ? 'Coupon Orders' :
                                                                        viewSingleData.userType === 'NO_COUPON_ORDERS' ? 'No Coupon Orders' :
                                                                            viewSingleData.userType === 'REFERRING_USERS' ? 'Referring Users' :
                                                                                viewSingleData.userType === 'REFERRED_USERS' ? 'Referred Users' :
                                                                                    viewSingleData.userType === 'REPEATED_USERS_DAYS' ? 'Repeated Users (Days)' :
                                                                                        viewSingleData.userType}
                                                            </strong>
                                                        </div>
                                                    ) : (
                                                        <div className="mb-2">
                                                            <small className="text-muted d-block">User Type:</small>
                                                            <strong>All Users</strong>
                                                        </div>
                                                    )}

                                                    {/* Date Range */}
                                                    {(viewSingleData.fromDate || viewSingleData.toDate) && (
                                                        <>
                                                            <div className="mb-2">
                                                                <small className="text-muted d-block">Date Range:</small>
                                                                <div className="d-flex gap-2">
                                                                    {viewSingleData.fromDate && (
                                                                        <span className="badge bg-light text-dark">
                                                                            From: {formatDate(viewSingleData.fromDate)}
                                                                        </span>
                                                                    )}
                                                                    {viewSingleData.toDate && (
                                                                        <span className="badge bg-light text-dark">
                                                                            To: {formatDate(viewSingleData.toDate)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>

                                    {/* Additional Filters */}
                                    <Row>
                                        {/* Price Range */}
                                        {(viewSingleData.minPrice || viewSingleData.maxPrice) && (
                                            <Col md={6}>
                                                <Card className="mb-3">
                                                    <Card.Header className="bg-light">
                                                        <h6 className="mb-0">
                                                            <IndianRupee size={16} className="me-2" />
                                                            Price Range
                                                        </h6>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <div className="d-flex justify-content-between">
                                                            {viewSingleData.minPrice && (
                                                                <div>
                                                                    <small className="text-muted d-block">Min Price:</small>
                                                                    <strong>₹{viewSingleData.minPrice}</strong>
                                                                </div>
                                                            )}
                                                            {viewSingleData.maxPrice && (
                                                                <div>
                                                                    <small className="text-muted d-block">Max Price:</small>
                                                                    <strong>₹{viewSingleData.maxPrice}</strong>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        )}

                                        {/* Food Type */}
                                        {viewSingleData.foodType && viewSingleData.foodType !== 'ALL' && (
                                            <Col md={6}>
                                                <Card className="mb-3">
                                                    <Card.Header className="bg-light">
                                                        <h6 className="mb-0">
                                                            <Package size={16} className="me-2" />
                                                            Food Type
                                                        </h6>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <div className="mb-2">
                                                            <small className="text-muted d-block">Filter:</small>
                                                            <strong>
                                                                {viewSingleData.foodType === 'VEG' ? 'Vegetarian' :
                                                                    viewSingleData.foodType === 'NON_VEG' ? 'Non-Vegetarian' :
                                                                        viewSingleData.foodType === 'VEGAN' ? 'Vegan' :
                                                                            viewSingleData.foodType}
                                                            </strong>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        )}
                                    </Row>

                                    {/* Target Specifics */}
                                    <Row>
                                        {/* Outlet */}
                                        {viewSingleData.outlet && (
                                            <Col md={6}>
                                                <Card className="mb-3">
                                                    <Card.Header className="bg-light">
                                                        <h6 className="mb-0">
                                                            <Store size={16} className="me-2" />
                                                            Outlet
                                                        </h6>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <div className="mb-2">
                                                            <small className="text-muted d-block">Outlet Name:</small>
                                                            <strong>{viewSingleData.outlet.name}</strong>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        )}

                                        {/* Category */}
                                        {viewSingleData.category && (
                                            <Col md={6}>
                                                <Card className="mb-3">
                                                    <Card.Header className="bg-light">
                                                        <h6 className="mb-0">
                                                            <Tag size={16} className="me-2" />
                                                            Category
                                                        </h6>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <div className="mb-2">
                                                            <small className="text-muted d-block">Category Name:</small>
                                                            <strong>{viewSingleData.category.name}</strong>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        )}

                                        {/* Product */}
                                        {viewSingleData.product && (
                                            <Col md={6}>
                                                <Card className="mb-3">
                                                    <Card.Header className="bg-light">
                                                        <h6 className="mb-0">
                                                            <Package size={16} className="me-2" />
                                                            Product
                                                        </h6>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <div className="mb-2">
                                                            <small className="text-muted d-block">Product Name:</small>
                                                            <strong>{viewSingleData.product.name}</strong>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        )}
                                    </Row>

                                    {/* Meta Information */}
                                    {/* <Card className="mt-3">
                                        <Card.Header className="bg-light">
                                            <h6 className="mb-0">
                                                <Clock size={16} className="me-2" />
                                                Meta Information
                                            </h6>
                                        </Card.Header>
                                        <Card.Body>
                                            <Row>
                                                <Col md={6}>
                                                    <div className="mb-2">
                                                        <small className="text-muted d-block">Created At:</small>
                                                        <strong>{formatDateTime(viewSingleData.createdAt)}</strong>
                                                    </div>
                                                </Col>
                                                <Col md={6}>
                                                    <div className="mb-2">
                                                        <small className="text-muted d-block">Last Updated:</small>
                                                        <strong>{formatDateTime(viewSingleData.updatedAt)}</strong>
                                                    </div>
                                                </Col>
                                                <Col md={12}>
                                                    <div className="mb-2">
                                                        <small className="text-muted d-block">Notification ID:</small>
                                                        <code className="text-muted">{viewSingleData._id}</code>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card> */}

                                    {/* Status Summary */}
                                    <div className="mt-4 p-3 rounded bg-light">
                                        <h6 className="mb-2">Current Status:</h6>
                                        <div className="d-flex align-items-center gap-3">
                                            <div>
                                                <small className="text-muted d-block">Schedule Status:</small>
                                                {getStatusBadge(viewSingleData.startDate, viewSingleData.endDate)}
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Notification Type:</small>
                                                {getTypeBadge(viewSingleData.type)}
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Target Users:</small>
                                                <Badge bg="info" className="ms-1">
                                                    {viewSingleData.userType ? 'Specific Users' : 'All Users'}
                                                </Badge>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2 mb-0">Loading notification details...</p>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowViewModal(false)}>
                                Close
                            </Button>
                            {/* {viewSingleData && (
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                        setShowViewModal(false);
                                        handleEditNotification(viewSingleData);
                                    }}
                                    disabled={!notificationAccess?.includes("NotificationScheduler:edit")}
                                >
                                    <Edit size={16} className="me-2" />
                                    Edit Notification
                                </Button>
                            )} */}
                        </Modal.Footer>
                    </Modal>
                )}
            </div>
        </>
    );
};

export default NotificationScheduler;